import React, { useRef, useState, useEffect } from 'react';
import { Stage, Layer, Line, Rect, Circle } from 'react-konva';
import styled from 'styled-components';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin: 20px;
`;

const ToolBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const ToolButton = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.active ? '#4CAF50' : '#ddd'};
  background: ${props => props.active ? '#4CAF50' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    border-color: #4CAF50;
  }
`;

const ColorPicker = styled.input`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

const SaveButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  font-size: 16px;

  &:hover {
    background: #45a049;
  }
`;

const CanvasContainer = styled.div`
  border: 2px solid #ddd;
  border-radius: 4px;
  background: white;
`;

interface DoodleEditorProps {
  onSave: (imageData: string) => void;
  initialIcon?: string;
}

type Tool = 'pen' | 'eraser' | 'circle' | 'square' | 'clear';

export const DoodleEditor: React.FC<DoodleEditorProps> = ({ onSave, initialIcon }) => {
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(2);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lines, setLines] = useState<any[]>([]);
  const stageRef = useRef<any>(null);

  const CANVAS_SIZE = 320; // 32x32 pixels scaled up 10x for easier editing
  const GRID_SIZE = 10; // Visual grid for better precision

  useEffect(() => {
    // Load initial icon if provided
    if (initialIcon) {
      // TODO: Load existing icon data
    }
  }, [initialIcon]);

  const handleMouseDown = (e: any) => {
    setIsDrawing(true);
    const pos = e.target.getStage().getPointerPosition();
    const relativePos = {
      x: pos.x,
      y: pos.y,
    };

    if (tool === 'clear') {
      setLines([]);
      return;
    }

    if (tool === 'pen' || tool === 'eraser') {
      setLines([...lines, {
        tool,
        points: [relativePos.x, relativePos.y],
        color: tool === 'eraser' ? '#FFFFFF' : color,
        size: brushSize,
      }]);
    } else if (tool === 'circle' || tool === 'square') {
      setLines([...lines, {
        tool,
        startPos: relativePos,
        endPos: relativePos,
        color,
        size: brushSize,
      }]);
    }
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawing) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const relativePos = {
      x: point.x,
      y: point.y,
    };

    const lastLine = lines[lines.length - 1];
    if (!lastLine) return;

    if (tool === 'pen' || tool === 'eraser') {
      const newPoints = lastLine.points.concat([relativePos.x, relativePos.y]);
      const newLines = [...lines];
      newLines[newLines.length - 1] = { ...lastLine, points: newPoints };
      setLines(newLines);
    } else if (tool === 'circle' || tool === 'square') {
      const newLines = [...lines];
      newLines[newLines.length - 1] = { ...lastLine, endPos: relativePos };
      setLines(newLines);
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const exportImage = () => {
    if (!stageRef.current) return;

    const stage = stageRef.current;
    const dataURL = stage.toDataURL({
      width: 32,
      height: 32,
      scaleX: 32 / CANVAS_SIZE,
      scaleY: 32 / CANVAS_SIZE,
    });

    // Convert to base64 without data URL prefix
    const base64Data = dataURL.split(',')[1];
    onSave(base64Data);
  };

  const renderGridLines = () => {
    const gridLines = [];

    // Vertical lines
    for (let i = 0; i <= CANVAS_SIZE; i += GRID_SIZE) {
      gridLines.push(
        <Line
          key={`v-${i}`}
          points={[i, 0, i, CANVAS_SIZE]}
          stroke="#f0f0f0"
          strokeWidth={1}
        />
      );
    }

    // Horizontal lines
    for (let i = 0; i <= CANVAS_SIZE; i += GRID_SIZE) {
      gridLines.push(
        <Line
          key={`h-${i}`}
          points={[0, i, CANVAS_SIZE, i]}
          stroke="#f0f0f0"
          strokeWidth={1}
        />
      );
    }

    return gridLines;
  };

  return (
    <EditorContainer>
      <h2>涂鸦编辑器</h2>
      <p>创建你的个人标识 (32x32 像素)</p>

      <ToolBar>
        <ToolButton active={tool === 'pen'} onClick={() => setTool('pen')}>
          画笔
        </ToolButton>
        <ToolButton active={tool === 'eraser'} onClick={() => setTool('eraser')}>
          橡皮擦
        </ToolButton>
        <ToolButton active={tool === 'circle'} onClick={() => setTool('circle')}>
          圆形
        </ToolButton>
        <ToolButton active={tool === 'square'} onClick={() => setTool('square')}>
          矩形
        </ToolButton>
        <ToolButton onClick={() => setLines([])}>
          清空
        </ToolButton>

        <ColorPicker
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
        />

        <div>
          <label>
            画笔大小:
            <input
              type="range"
              min="1"
              max="10"
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
            />
            {brushSize}px
          </label>
        </div>
      </ToolBar>

      <CanvasContainer>
        <Stage
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          onMouseDown={handleMouseDown}
          onMousemove={handleMouseMove}
          onMouseup={handleMouseUp}
          ref={stageRef}
        >
          <Layer>
            {/* Background */}
            <Rect
              x={0}
              y={0}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              fill="white"
            />

            {/* Grid */}
            {renderGridLines()}

            {/* Drawing */}
            {lines.map((line, i) => {
              if (line.tool === 'pen' || line.tool === 'eraser') {
                return (
                  <Line
                    key={i}
                    points={line.points}
                    stroke={line.color}
                    strokeWidth={line.size}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={
                      line.tool === 'eraser' ? 'destination-out' : 'source-over'
                    }
                  />
                );
              } else if (line.tool === 'circle') {
                const radius = Math.sqrt(
                  Math.pow(line.endPos.x - line.startPos.x, 2) +
                  Math.pow(line.endPos.y - line.startPos.y, 2)
                );
                return (
                  <Circle
                    key={i}
                    x={line.startPos.x}
                    y={line.startPos.y}
                    radius={radius}
                    stroke={line.color}
                    strokeWidth={line.size}
                  />
                );
              } else if (line.tool === 'square') {
                return (
                  <Rect
                    key={i}
                    x={Math.min(line.startPos.x, line.endPos.x)}
                    y={Math.min(line.startPos.y, line.endPos.y)}
                    width={Math.abs(line.endPos.x - line.startPos.x)}
                    height={Math.abs(line.endPos.y - line.startPos.y)}
                    stroke={line.color}
                    strokeWidth={line.size}
                  />
                );
              }
              return null;
            })}
          </Layer>
        </Stage>
      </CanvasContainer>

      <SaveButton onClick={exportImage}>
        保存标识
      </SaveButton>
    </EditorContainer>
  );
};